/**
 * Audit Log Service for ArenaSync
 *
 * Provides client-side helpers to log activity events.
 * Device type is auto-detected from the user agent.
 * All writes go through the activity_logs table (INSERT only).
 *
 * Usage:
 *   import { logActivity } from '@/lib/services/auditService';
 *   await logActivity(supabase, { arenaId, actorUserId, action, module, description, deviceType });
 */

import { SupabaseClient } from '@supabase/supabase-js';

export type DeviceType = 'Desktop/Laptop' | 'Mobile' | 'Tablet';

export interface ActivityLogPayload {
  arenaId: string;
  actorUserId: string;
  action: string;
  module: string;
  entityType?: string;
  entityId?: string;
  description: string;
  deviceType?: DeviceType;
}

/**
 * Detect device type from navigator.userAgent.
 * Returns 'Mobile', 'Tablet', or 'Desktop/Laptop'.
 */
export function detectDeviceType(): DeviceType {
  if (typeof navigator === 'undefined') return 'Desktop/Laptop';
  const ua = navigator.userAgent.toLowerCase();
  // Tablet check first (iPads, Android tablets)
  if (
    /ipad/.test(ua) ||
    (/android/.test(ua) && !/mobile/.test(ua)) ||
    /tablet/.test(ua)
  ) {
    return 'Tablet';
  }
  // Mobile check
  if (
    /iphone|ipod|android.*mobile|windows phone|blackberry|bb\d+|meego|silk|opera mini|opera mobi/.test(ua)
  ) {
    return 'Mobile';
  }
  return 'Desktop/Laptop';
}

/**
 * Insert an activity log record.
 * Silently fails — audit logging must never break the main flow.
 * On failure the full error object (including PostgreSQL error code) is
 * logged to the console so RLS / constraint rejections are visible during
 * debugging without surfacing to the end user.
 */
export async function logActivity(
  supabase: SupabaseClient,
  payload: ActivityLogPayload
): Promise<void> {
  try {
    const deviceType = payload.deviceType ?? detectDeviceType();
    const { error } = await supabase.from('activity_logs').insert({
      arena_id: payload.arenaId,
      actor_user_id: payload.actorUserId,
      action: payload.action,
      module: payload.module,
      entity_type: payload.entityType ?? null,
      entity_id: payload.entityId ?? null,
      description: payload.description,
      device_type: deviceType,
    });
    if (error) {
      console.error('[auditService] Failed to log activity:', {
        action: payload.action,
        module: payload.module,
        code: (error as any).code,
        message: error.message,
        details: (error as any).details,
        hint: (error as any).hint,
        error,
      });
    }
  } catch (err) {
    console.error('[auditService] Unexpected error logging activity:', err);
  }
}

// ── Pre-built event helpers ───────────────────────────────────────────────────

export async function logLogin(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'login',
    module: 'authentication',
    entityType: 'staff',
    entityId: actorUserId,
    description: `${actorName} signed in`,
  });
}

export async function logLogout(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'logout',
    module: 'authentication',
    entityType: 'staff',
    entityId: actorUserId,
    description: `${actorName} signed out`,
  });
}

export async function logStaffCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.created',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} created staff member ${targetName}`,
  });
}

export async function logStaffUpdated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.updated',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} updated staff member ${targetName}`,
  });
}

export async function logStaffActivated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.activated',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} activated staff member ${targetName}`,
  });
}

export async function logStaffDeactivated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.deactivated',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} deactivated staff member ${targetName}`,
  });
}

export async function logStaffRoleAssigned(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string,
  roleName: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.role_assigned',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} assigned role "${roleName}" to ${targetName}`,
  });
}

export async function logStaffRoleRemoved(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string,
  roleName: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.role_removed',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} removed role "${roleName}" from ${targetName}`,
  });
}

export async function logStaffPositionChanged(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  targetName: string,
  targetId: string,
  positionName: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.position_changed',
    module: 'staff',
    entityType: 'staff',
    entityId: targetId,
    description: `${actorName} changed position of ${targetName} to "${positionName}"`,
  });
}

// ── Staff Invitation event helpers ────────────────────────────────────────────

export async function logStaffInvitationCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  inviteeName: string,
  invitationId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.invitation_created',
    module: 'staff',
    entityType: 'staff_invitation',
    entityId: invitationId,
    description: `${actorName} sent a staff invitation to ${inviteeName}`,
  });
}

export async function logStaffInvitationResent(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  inviteeName: string,
  invitationId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.invitation_resent',
    module: 'staff',
    entityType: 'staff_invitation',
    entityId: invitationId,
    description: `${actorName} resent staff invitation to ${inviteeName}`,
  });
}

export async function logStaffInvitationRevoked(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  inviteeName: string,
  invitationId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.invitation_revoked',
    module: 'staff',
    entityType: 'staff_invitation',
    entityId: invitationId,
    description: `${actorName} revoked staff invitation for ${inviteeName}`,
  });
}

export async function logStaffInvitationAccepted(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  inviteeName: string,
  invitationId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'staff.invitation_accepted',
    module: 'staff',
    entityType: 'staff_invitation',
    entityId: invitationId,
    description: `${inviteeName} accepted staff invitation`,
  });
}

// ── Member event helpers ──────────────────────────────────────────────────────

export async function logMemberCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.created',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} added new member ${memberName} (ID: ${memberId})`,
  });
}

export async function logMemberUpdated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.updated',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} updated member ${memberName} (ID: ${memberId})`,
  });
}

export async function logMemberDeleted(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  memberPhone: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.deleted',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} deleted member ${memberName} (ID: ${memberId}, Phone: ${memberPhone})`,
  });
}

export async function logMemberActivated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.activated',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} activated member ${memberName} (ID: ${memberId})`,
  });
}

export async function logMemberDeactivated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.deactivated',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} deactivated member ${memberName} (ID: ${memberId})`,
  });
}

// ── Membership Enrollment event helpers ───────────────────────────────────────

export async function logMembershipEnrollmentCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  planName: string,
  sportName: string,
  enrollmentId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'membership.enrollment_created',
    module: 'members',
    entityType: 'membership_enrollment',
    entityId: enrollmentId,
    description: `${actorName} enrolled ${memberName} (ID: ${memberId}) in ${sportName} — ${planName}`,
  });
}

export async function logMemberSuspended(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  reason?: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.suspended',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} suspended member ${memberName} (ID: ${memberId})${reason ? ` — Reason: ${reason}` : ''}`,
  });
}

export async function logMemberReactivated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'member.reactivated',
    module: 'members',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} reactivated member ${memberName} (ID: ${memberId})`,
  });
}

export async function logMembershipSuspended(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  enrollmentId: string,
  planName: string,
  reason: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'membership.suspended',
    module: 'members',
    entityType: 'membership_enrollment',
    entityId: enrollmentId,
    description: `${actorName} suspended membership "${planName}" for ${memberName} — Reason: ${reason}`,
  });
}

// ── Payment & Charge Audit Events ─────────────────────────────────────────────

export async function logChargeCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  description: string,
  amount: number,
  source: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'charge.created',
    module: 'payments',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} created ${source} charge for ${memberName}: ₹${amount.toLocaleString('en-IN')} — ${description}`,
  });
}

export async function logPaymentCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  amount: number,
  method: string,
  reference?: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'payment.created',
    module: 'payments',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} recorded payment of ₹${amount.toLocaleString('en-IN')} from ${memberName} via ${method}${reference ? ` (Ref: ${reference})` : ''}`,
  });
}

export async function logChargeWaived(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  description: string,
  amount: number,
  reason: string
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'charge.waived',
    module: 'payments',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} waived charge for ${memberName}: ₹${amount.toLocaleString('en-IN')} — ${description}. Reason: ${reason}`,
  });
}

export async function logAddonChargeCreated(
  supabase: SupabaseClient,
  arenaId: string,
  actorUserId: string,
  actorName: string,
  memberName: string,
  memberId: string,
  addonName: string,
  amount: number
): Promise<void> {
  await logActivity(supabase, {
    arenaId,
    actorUserId,
    action: 'charge.created',
    module: 'payments',
    entityType: 'member',
    entityId: memberId,
    description: `${actorName} added add-on charge for ${memberName}: ${addonName} — ₹${amount.toLocaleString('en-IN')}`,
  });
}
